// ============================================================================
// Sovereign Identity — the authority name, project anchor and agent id the
// bridge speaks for, plus the passphrase-derived key that seals every record.
// Identity is plain (it is a claim, not a secret). The passphrase never leaves
// this device: only a PBKDF2-derived AES-GCM key lives in memory, and a small
// encrypted verifier is stored so a wrong passphrase can be detected.
// ============================================================================

export interface SovereignIdentity {
  authority: string;
  projectId: string;
  agent: string;
  saltB64: string;
}

const ID_KEY = "sophia:sovereign:identity:v1";
const VERIFIER_KEY = "sophia:sovereign:verifier:v1";
const VERIFIER_PLAIN = "PISTIS-SOPHIA-SOVEREIGN-VERIFIER";

export const DEFAULT_IDENTITY: SovereignIdentity = {
  authority: "Skye Star McDade",
  projectId: "b33bfd67-f8c1-4c76-9b0d-ef29c0f4772f",
  agent: "agent#00000002866461",
  saltB64: "",
};

export function b64(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}
export function unb64(text: string): Uint8Array {
  const raw = atob(text);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function randomSalt(): string {
  return b64(crypto.getRandomValues(new Uint8Array(16)));
}

export function getIdentity(): SovereignIdentity {
  if (typeof window === "undefined") return { ...DEFAULT_IDENTITY, saltB64: "" };
  try {
    const raw = localStorage.getItem(ID_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<SovereignIdentity>;
      const merged: SovereignIdentity = {
        authority: parsed.authority?.trim() || DEFAULT_IDENTITY.authority,
        projectId: parsed.projectId?.trim() || DEFAULT_IDENTITY.projectId,
        agent: parsed.agent?.trim() || DEFAULT_IDENTITY.agent,
        saltB64: parsed.saltB64 || randomSalt(),
      };
      if (!parsed.saltB64) localStorage.setItem(ID_KEY, JSON.stringify(merged));
      return merged;
    }
  } catch {}
  const fresh = { ...DEFAULT_IDENTITY, saltB64: randomSalt() };
  try {
    localStorage.setItem(ID_KEY, JSON.stringify(fresh));
  } catch {}
  return fresh;
}

export function saveIdentity(patch: Partial<SovereignIdentity>): SovereignIdentity {
  const next: SovereignIdentity = { ...getIdentity(), ...patch };
  next.authority = next.authority.trim() || DEFAULT_IDENTITY.authority;
  next.projectId = next.projectId.trim() || DEFAULT_IDENTITY.projectId;
  next.agent = next.agent.trim() || DEFAULT_IDENTITY.agent;
  if (!next.saltB64) next.saltB64 = randomSalt();
  try {
    localStorage.setItem(ID_KEY, JSON.stringify(next));
  } catch {}
  notify();
  return next;
}

// --- authority chain -------------------------------------------------------

export interface AuthorityLink {
  at: string;
  authority: string;
  projectId: string;
  agent: string;
}
const CHAIN_KEY = "sophia:sovereign:chain:v1";

export function authorityChain(): AuthorityLink[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(CHAIN_KEY);
    return raw ? (JSON.parse(raw) as AuthorityLink[]) : [];
  } catch {
    return [];
  }
}

export function recordAuthorityLink(id: SovereignIdentity): AuthorityLink[] {
  const chain = authorityChain();
  const last = chain[chain.length - 1];
  if (
    last &&
    last.authority === id.authority &&
    last.projectId === id.projectId &&
    last.agent === id.agent
  ) {
    return chain;
  }
  const next = [
    ...chain,
    { at: new Date().toISOString(), authority: id.authority, projectId: id.projectId, agent: id.agent },
  ].slice(-50);
  try {
    localStorage.setItem(CHAIN_KEY, JSON.stringify(next));
  } catch {}
  return next;
}

// --- key handling ----------------------------------------------------------

let sealKey: CryptoKey | null = null;
const listeners = new Set<() => void>();

export function subscribeSovereign(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function notify(): void {
  for (const fn of [...listeners]) {
    try {
      fn();
    } catch {}
  }
}

export function isUnlocked(): boolean {
  return sealKey !== null;
}
export function currentKey(): CryptoKey | null {
  return sealKey;
}
export function hasPassphrase(): boolean {
  if (typeof window === "undefined") return false;
  return !!localStorage.getItem(VERIFIER_KEY);
}

async function deriveKey(passphrase: string, saltB64: string): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey("raw", new TextEncoder().encode(passphrase), "PBKDF2", false, [
    "deriveKey",
  ]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: unb64(saltB64) as unknown as BufferSource, iterations: 150_000, hash: "SHA-256" },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

async function sealWith(key: CryptoKey, plain: string): Promise<{ ciphertext: string; iv: string }> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const buf = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv as unknown as BufferSource },
    key,
    new TextEncoder().encode(plain),
  );
  return { ciphertext: b64(new Uint8Array(buf)), iv: b64(iv) };
}
async function openWith(key: CryptoKey, ciphertext: string, iv: string): Promise<string> {
  const buf = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: unb64(iv) as unknown as BufferSource },
    key,
    unb64(ciphertext) as unknown as BufferSource,
  );
  return new TextDecoder().decode(buf);
}

/** First-time setup (or reset) of the Sovereign passphrase. */
export async function setPassphrase(passphrase: string): Promise<void> {
  if (passphrase.length < 6) throw new Error("Passphrase must be at least 6 characters.");
  const id = getIdentity();
  const key = await deriveKey(passphrase, id.saltB64);
  const verifier = await sealWith(key, VERIFIER_PLAIN);
  localStorage.setItem(VERIFIER_KEY, JSON.stringify(verifier));
  sealKey = key;
  notify();
}

/** Unlock with an existing passphrase. Throws when it does not match. */
export async function unlock(passphrase: string): Promise<void> {
  const id = getIdentity();
  const key = await deriveKey(passphrase, id.saltB64);
  const raw = localStorage.getItem(VERIFIER_KEY);
  if (!raw) {
    await setPassphrase(passphrase);
    return;
  }
  const { ciphertext, iv } = JSON.parse(raw) as { ciphertext: string; iv: string };
  let plain = "";
  try {
    plain = await openWith(key, ciphertext, iv);
  } catch {
    throw new Error("That passphrase does not match the Sovereign seal.");
  }
  if (plain !== VERIFIER_PLAIN) throw new Error("That passphrase does not match the Sovereign seal.");
  sealKey = key;
  notify();
}

export function lockVault(): void {
  sealKey = null;
  notify();
}

export async function sealJson(value: unknown): Promise<{ ciphertext: string; iv: string }> {
  if (!sealKey) throw new Error("Vault is locked.");
  return sealWith(sealKey, JSON.stringify(value));
}
export async function openJson<T>(ciphertext: string, iv: string): Promise<T> {
  if (!sealKey) throw new Error("Vault is locked.");
  return JSON.parse(await openWith(sealKey, ciphertext, iv)) as T;
}
